<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">How-To Guide</span>
      <h1>How to Check Your Steam Hours</h1>
      <p>
        Steam records how many hours you have on every game, but it spreads that number across the library,
        your profile and your reviews — and never adds it up. Here is where to find each figure in Steam itself,
        what Steam will not show you, and how to get the rest.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Check my Steam hours</router-link>
        <a class="seo-secondary-link" href="#in-steam">Where Steam shows it →</a>
      </div>
      <ul class="seo-highlight-list">
        <li>
          <span class="seo-highlight-icon">🎮</span>
          <div>Per game: the Steam library and your profile's Games page both show hours on record.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">➕</span>
          <div>Account total: Steam has no such number — you have to add it up, or let a tool do it.</div>
        </li>
        <li>
          <span class="seo-highlight-icon">🗓️</span>
          <div>Per day: Steam keeps no daily record at all, only a rolling two-week figure.</div>
        </li>
      </ul>
    </section>

    <section class="seo-section" id="in-steam">
      <h2>Where Steam Shows Your Hours</h2>
      <p>
        There are three places in Steam that show playtime. All three are per game — none of them gives you a
        total or a history.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>1. Your Library</h3>
          <p>In the Steam client, open <strong>Library</strong> and select a game. The bar under the banner shows <strong>Play Time</strong> — your lifetime hours on that game — and when you last played it.</p>
        </div>
        <div class="seo-card">
          <h3>2. Your Profile's Games Page</h3>
          <p>Open your profile and choose <strong>Games</strong>. Every game is listed with its <strong>hours on record</strong>, and you can sort by playtime. Your profile's Recent Activity adds hours played in the past two weeks.</p>
        </div>
        <div class="seo-card">
          <h3>3. Your Reviews</h3>
          <p>Every Steam review shows the reviewer's <strong>hrs on record</strong>, plus how many they had when they wrote it — that is the same lifetime per-game figure.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>What "Hours on Record" Means</h2>
      <p>
        "Hours on record" is the lifetime total Steam has logged for one game on your account — every session,
        online and offline, since you first launched it. It only ever goes up, and it belongs to that single game:
        Steam does not sum it across your library, and it does not tell you <em>when</em> those hours were played.
      </p>
      <div class="seo-stats">
        <div class="seo-stat">
          <div class="label">Per game</div>
          <div class="value">In Steam</div>
        </div>
        <div class="seo-stat">
          <div class="label">Account total</div>
          <div class="value">Not in Steam</div>
        </div>
        <div class="seo-stat">
          <div class="label">Per day</div>
          <div class="value">Not in Steam</div>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Getting the Numbers Steam Leaves Out</h2>
      <p>
        steamplayti.me reads the same hours on record from Steam's API and adds every game together into one
        all-time total that sits on your dashboard. It also saves a snapshot every day, so you get the per-day and
        per-month breakdown Steam never keeps.
      </p>
      <div class="seo-grid">
        <div class="seo-card">
          <h3>Sign in through Steam</h3>
          <p>Tracking your own profile starts with a Steam sign-in, so we know the profile is yours. We never see your password.</p>
        </div>
        <div class="seo-card">
          <h3>Make game details public</h3>
          <p>Steam only shares playtime from profiles whose <strong>Game details</strong> are set to Public in the privacy settings.</p>
        </div>
        <div class="seo-card">
          <h3>Come back tomorrow</h3>
          <p>The total is there straight away; daily charts start filling in from your first full day of tracking.</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>Checking Steam Hours: FAQ</h2>
      <div class="seo-faq">
        <div class="seo-faq-item">
          <h3>How do I see my total hours on Steam?</h3>
          <p>Steam does not show one. It shows hours per game only, so the total means adding up every game on your profile's Games page — or adding your profile to steamplayti.me, which does the sum for you.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Can I check someone else's Steam hours?</h3>
          <p>Only if they allow it. If their profile's Game details are Public, their Games page shows hours on record for each game, the same as yours. If they are private, nobody can see them.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Why can't anyone see my playtime?</h3>
          <p>Check Steam's privacy settings: Game details must be Public, and the option to always keep your total playtime private must be unticked. Either one hides your hours.</p>
        </div>
        <div class="seo-faq-item">
          <h3>Can I see how many hours I played on a specific day?</h3>
          <p>Not in Steam — it only keeps lifetime totals and a two-week figure. steamplayti.me records a daily snapshot from the day you start tracking; days before that cannot be recovered from Steam.</p>
        </div>
      </div>
    </section>
    <SeoRelatedTools current-slug="how-to-check-steam-hours" />
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

const META_DESCRIPTION = 'Steam never shows your total hours across all games. Here is where to find hours per game in the library and profile, and how to get your all-time total and daily playtime in one place.';

const META = {
  title: 'How to See Your Total Hours on Steam (All Games) | steamplayti.me',
  description: META_DESCRIPTION,
  keywords: 'how to check steam hours, check steam hours, steam hours on record, how to see playtime on steam, check steam playtime',
  canonical: 'https://steamplayti.me/page/how-to-check-steam-hours/',
  ogTitle: 'How to Check Your Steam Hours',
  ogDescription: 'Every place Steam shows your hours, what it leaves out, and how to get your total and daily playtime.',
  ogUrl: 'https://steamplayti.me/page/how-to-check-steam-hours/',
  twitterTitle: 'How to Check Your Steam Hours',
  twitterDescription: 'Where Steam shows your hours on record — and how to get the total it never adds up.',
  structuredData: pageStructuredData({
    name: 'How to Check Your Steam Hours',
    description: META_DESCRIPTION,
    canonical: 'https://steamplayti.me/page/how-to-check-steam-hours/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'How to Check Steam Hours', path: 'https://steamplayti.me/page/how-to-check-steam-hours/' },
    ],
  }),
};

export default {
  name: 'HowToCheckSteamHoursPage',
  components: { SeoRelatedTools },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
